import {
  LitElement,
  paths,
  html,
  unsafeHTML,
  css,
  utils,
  ifDefined,
} from '@hh/global.js'
const { externalVideoEmbedSrc, getPreviousSibling } = utils

class AdaptiveVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    id: {
      type: String,
    },
    title: {
      type: String,
    },
    autoplay: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    host: {
      type: String,
    },
    description: {
      type: String,
    },
    aspectRatio: {
      type: String,
      attribute: 'aspect-ratio',
    },
    posterImage: {
      type: String,
      attribute: 'poster-image',
    },
    posterImageMobile: {
      type: String,
      attribute: 'poster-image-mobile',
    },
    useExternalPlayImage: {
      type: Boolean,
      attribute: 'use-external-play-image',
    },
    enableSound: {
      type: Boolean,
      attribute: 'enable-sound',
    },
  }

  get computedAspectRatio() {
    return (
      this.videoObject?.aspect_ratio ||
      (this.aspectRatio === 'wide' && 16 / 9) ||
      (this.aspectRatio === 'full' && 4 / 3) ||
      eval(this.aspectRatio) ||
      16 / 9
    )
  }

  get videoElement() {
    return this.renderRoot?.getElementById('video-element') ?? null
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.playing = this.autoplay
    this.style.setProperty('--hh-aspect-ratio', this.computedAspectRatio)
  }

  firstUpdated() {
    if (this.host === 'vimeo') {
      this.videoElement = new Vimeo.Player(this.videoElement)
    }
  }

  handlePlayClick() {
    if (this.playing == false) {
      this.playing = true
    }
    let overlay = this.renderRoot?.querySelector('.video-overlay')
    if (overlay) overlay.style.display = 'none'

    if (this.host === 'vimeo') {
      this.videoElement.play()
    } else if (this.host === 'youtube') {
      this.videoElement.src = this.videoElement.src.replace(
        'autoplay=0',
        'autoplay=1'
      )
      this.videoElement.focus()
    }
  }

  generatePosterImage() {
    const hasMobileAndDesktop = this.posterImage && this.posterImageMobile
    const mobileSource = this.posterImageMobile
    const desktopSource = this.posterImage

    return `${hasMobileAndDesktop ? `<picture>` : ''}
      ${
        hasMobileAndDesktop
          ? `<source media="(min-width: 1024px)" srcset="${desktopSource}">`
          : ''
      }
      <img src="${mobileSource || desktopSource}" alt="">
    ${hasMobileAndDesktop ? `</picture>` : ''}`
  }

  /**
   * Generate the YT video poster image for elements who use YT videos and don't have a poster image set in the shop admin
   * @returns {string} Image HTML
   */
  generateYTPosterImage() {
    if (!this.id) {
      return ''
    }

    return `<img src="//img.youtube.com/vi/${this.id}/hqdefault.jpg" alt="" loading="lazy">`
  }

  generatePosterWithText() {
    const isYTVideo = ['youtube'].includes(this.host)
    if (!isYTVideo && !this.posterImage) {
      return html``
    }

    const customImage = this.posterImage
      ? this.generatePosterImage()
      : this.generateYTPosterImage()
    const playImage = this.useExternalPlayImage
      ? paths.playExternal
      : paths.play

    return html`
      <div class="video-overlay">
        ${this.title ? html`<h2>${this.title}</h2>` : html``}
        ${this.description ? html`<p>${this.description}</p>` : html``}
        <button
          class="video-poster-button${this.useExternalPlayImage
            ? ' video-poster-button--external'
            : ''}"
          type="button"
          @click="${this.handlePlayClick}"
        >
          ${unsafeHTML(playImage || html`►`)}
        </button>
        ${unsafeHTML(customImage)}
      </div>
    `
  }

  renderVideo() {
    return html`
      <video
        id="video-element"
        playsinline="playsinline"
        ?autoplay="${this.autoplay}"
        ?controls="${this.controls}"
        ?loop="${this.loop}"
        poster="${ifDefined(this.posterImage)}"
        preload="none"
        muted="${this.enableSound === false}"
        aria-label="${this.description}"
      >
        ${this.videoObject.sources.map(
          (source) =>
            html`<source src="${source.url}" type="${source.mime_type}"></source>`
        )}
      </video>
    `
  }

  renderIframe() {
    return html`
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${externalVideoEmbedSrc(
          this.host,
          this.id,
          this.autoplay,
          this.controls,
          this.loop,
          this.enableSound === false
        )}"
        title="${this.title}"
      >
      </iframe>
    `
  }

  render() {
    return html`
      ${['vimeo', 'youtube'].includes(this.host)
        ? this.renderIframe()
        : this.renderVideo()}
      ${this.autoplay ? html`` : this.generatePosterWithText()}
    `
  }

  static styles = css`
    :host {
      aspect-ratio: var(--hh-aspect-ratio, 16 / 9);
      display: block;
      position: relative;
    }
    #video-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: var(--hh-object-fit, cover);
    }
    .video-overlay {
      position: absolute;
      display: flex;
      top: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    h2,
    p {
      z-index: 1;
      color: white;
    }
    h2 {
      font-size: 3rem;
      margin-bottom: 1.5rem;
    }
    p {
      font-size: 1.5rem;
      width: 25%;
      text-align: center;
      margin-bottom: 3rem;
    }
    .video-overlay img {
      position: absolute;
      top: 50%;
      max-width: 100%;
      left: 0;
      right: 0;
      transform: translateY(-50%);
      width: 100%;
      height: auto;
      filter: brightness(0.5);
    }
    .video-poster-button {
      align-items: center;
      background: transparent;
      border: 1px solid white;
      border-radius: 60px;
      cursor: pointer;
      width: 60px;
      height: 60px;
      padding: 1rem;
      display: flex;
      justify-content: center;
      z-index: 1;
    }
    .video-poster-button--external {
      border: none;
      height: auto;
      padding: 0;
      width: auto;
    }
    .video-poster-button svg {
      position: relative;
      width: 30px;
      height: 30px;
      fill: white;
      stroke: white;
    }
    .video-poster-button .icon-play-external {
      color: var(--hh-color-day, #fff);
      height: 48px;
      width: 48px;
    }

    @media screen and (min-width: 1024px) {
      .video-poster-button .icon-play-external {
        height: 96px;
        width: 96px;
      }
    }
  `
}

window.customElements.define('hh-adaptive-video', AdaptiveVideo)
